import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Briefcase, Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Auth({ mode }) {
  const isLogin = mode === "login";
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", intent: "worker" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const u = await login(form.email, form.password);
        toast.success(`Selamat datang, ${u.name}!`);
        navigate(u.role === "admin" ? "/admin" : "/app");
      } else {
        if (!form.name || form.name.length < 2) throw { response: { data: { detail: "Nama minimal 2 karakter" } } };
        if (form.password.length < 6) throw { response: { data: { detail: "Password minimal 6 karakter" } } };
        const u = await register(form);
        toast.success(`Akun dibuat. Halo, ${u.name}!`);
        navigate("/app");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <Link to="/" className="flex items-center gap-2.5">
          <LogoMark size={40} />
          <span className="font-extrabold text-xl tracking-tight">ANDIL</span>
        </Link>
        <div>
          <h2 className="text-4xl font-extrabold leading-tight">Temukan pekerjaan.<br /><span className="text-blue-400">Berikan kesempatan.</span></h2>
          <p className="text-slate-300 mt-4 max-w-md">Marketplace kerja fleksibel yang mempertemukan kebutuhan dengan kemampuan.</p>
        </div>
        <p className="text-slate-400 text-sm">Demo: sani@andil.demo / Demo123! · budi@andil.demo / Demo123!</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-8">
            <LogoMark size={36} />
            <span className="font-extrabold text-lg tracking-tight">ANDIL</span>
          </Link>
          <h1 className="text-3xl font-extrabold text-slate-900">{isLogin ? "Masuk" : "Daftar"}</h1>
          <p className="text-slate-500 mt-1">{isLogin ? "Selamat datang kembali di ANDIL." : "Buat akun untuk mulai."}</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label className="mb-1.5 block">Nama lengkap</Label>
                  <Input data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Budi Santoso" className="h-11" />
                </div>
                <div>
                  <Label className="mb-1.5 block">Nomor HP</Label>
                  <Input data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="0812xxxxxxxx" className="h-11" />
                </div>
              </>
            )}
            <div>
              <Label className="mb-1.5 block">Email</Label>
              <Input data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="nama@email.com" className="h-11" />
            </div>
            <div>
              <Label className="mb-1.5 block">Password</Label>
              <div className="relative">
                <Input data-testid="input-password" type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} placeholder="••••••" className="h-11 pr-10" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div>
                <Label className="mb-2 block">Anda ingin menggunakan ANDIL sebagai?</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button type="button" data-testid="intent-worker" onClick={() => setForm({ ...form, intent: "worker" })}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${form.intent === "worker" ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                    <Search size={20} className="text-blue-600 mb-1" />
                    <p className="font-semibold text-sm">Mencari pekerjaan</p>
                  </button>
                  <button type="button" data-testid="intent-employer" onClick={() => setForm({ ...form, intent: "employer" })}
                    className={`rounded-xl border-2 p-3 text-left transition-colors ${form.intent === "employer" ? "border-slate-900 bg-slate-50" : "border-slate-200"}`}>
                    <Briefcase size={20} className="text-slate-900 mb-1" />
                    <p className="font-semibold text-sm">Mencari tenaga</p>
                  </button>
                </div>
              </div>
            )}

            <Button data-testid="auth-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-blue-600 hover:bg-blue-700 rounded-lg text-base font-semibold">
              {loading ? "Memproses..." : isLogin ? "Masuk" : "Daftar"}
            </Button>
          </form>

          <p className="text-center text-slate-500 mt-6 text-sm">
            {isLogin ? "Belum punya akun? " : "Sudah punya akun? "}
            <Link data-testid="auth-switch-link" to={isLogin ? "/daftar" : "/masuk"} className="text-blue-600 font-semibold">
              {isLogin ? "Daftar" : "Masuk"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
